import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

import { ignorandoElPanel } from '@/lib/softphone/panel';
import { cn } from '@/lib/utils';
import { OVERLAY_SCRIM } from './dialog';
import './animations.css';

export const Sheet = SheetPrimitive.Root;
export const SheetTrigger = SheetPrimitive.Trigger;
export const SheetClose = SheetPrimitive.Close;

const sheetVariants = cva(
  'fixed z-50 flex flex-col gap-4 border-border bg-popover p-4 text-popover-foreground shadow-lg',
  {
    variants: {
      side: {
        right: 'ui-anim-sheet-right inset-y-0 right-0 h-svh w-full max-w-md border-l',
        left: 'ui-anim-sheet-left inset-y-0 left-0 h-svh w-full max-w-md border-r',
        bottom: 'ui-anim-sheet-bottom inset-x-0 bottom-0 max-h-[85svh] rounded-t-xl border-t',
      },
    },
    defaultVariants: { side: 'right' },
  },
);

export type SheetSide = NonNullable<VariantProps<typeof sheetVariants>['side']>;

export type SheetContentProps = React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: SheetSide;
  showClose?: boolean;
};

export function SheetContent({
  className,
  children,
  side = 'right',
  showClose = true,
  onInteractOutside,
  onPointerDownOutside,
  onFocusOutside,
  ...props
}: SheetContentProps) {
  return (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay className={cn('ui-anim-overlay fixed inset-0 z-50', OVERLAY_SCRIM)} />
      <SheetPrimitive.Content
        className={cn(sheetVariants({ side }), className)}
        onInteractOutside={ignorandoElPanel(onInteractOutside)}
        onPointerDownOutside={ignorandoElPanel(onPointerDownOutside)}
        onFocusOutside={ignorandoElPanel(onFocusOutside)}
        {...props}
      >
        {children}
        {showClose ? (
          <SheetPrimitive.Close
            aria-label="Cerrar"
            className="absolute right-3 top-3 inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" />
          </SheetPrimitive.Close>
        ) : null}
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
}

export function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('flex flex-col gap-1 pr-8', className)} {...props} />;
}

export function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      className={cn('text-base font-medium leading-none tracking-[-0.01em]', className)}
      {...props}
    />
  );
}

export function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('mt-auto flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}
